import {Component, markRaw, ref, type Ref} from "vue";

export type ComponentProps<T> = T extends abstract new (...args: any) => any
    ? InstanceType<T>['$props']
    : T extends (props: infer P, ...args: any) => any
        ? P
        : Record<string, any>;

type ComponentPropsWithoutModal<T> = Omit<ComponentProps<T>, 'modal'>;

type WMData = {
    sideModals: WMModal[],
    modals: WMModal[],
}

let wmData: Ref<WMData>;

class WMModal {
    protected closePromise: Promise<any>|null = null;

    protected closePromiseResolver: ((value:any)=>void)|null = null;

    waitForClose(): Promise<any> {
        if (!this.closePromise) {
            this.closePromise = new Promise<any>(resolve => {
                this.closePromiseResolver = resolve;
            });
        }

        return this.closePromise;
    }

    async close(value: any = undefined) {
        // Only a strict `false` vetoes the close (docs/tasks/049.md) — `undefined`/`true`/anything
        // else proceeds, so a caller that doesn't care about the result can keep onClose sync and
        // return nothing.
        if (this.onClose && await this.onClose(value) === false) return;

        if (this.closePromiseResolver) this.closePromiseResolver(value);

        const data = getWmData();
        data.value.modals = data.value.modals.filter(modal => modal !== this);
        data.value.sideModals = data.value.sideModals.filter(modal => modal !== this);
    }

    constructor(
        public component: Component,
        public props: Record<string, any>,
        public title: string = '',
        public isCloseable: boolean = false,
        public onClose: ((value: any) => boolean | Promise<boolean>) | null = null,
    ) {
    }
}

export type { WMModal };

const getWmData = (): Ref<WMData> => {
    if (!wmData) {
        wmData = ref({
            sideModals: [],
            modals: [],
        });
    }

    return wmData;
}

const addWindow = <T extends Component>(section: keyof WMData, component: T, props: ComponentPropsWithoutModal<T>, title: string, isCloseable: boolean = false, onClose: ((value: any) => boolean | Promise<boolean>) | null = null) => {
    const wmData = getWmData();
    // markRaw: component is a raw SFC definition, not app state — letting Vue's reactivity wrap it
    // (since the WMModal instance holding it lives inside a ref()-backed array) defeats
    // <component :is="..."> identity tracking and produces "Vue received a Component that was made
    // a reactive object" at runtime (docs/tasks/026-proc-editing.md część 2 — reported as the admin
    // list not reflecting a just-saved edit until a full page reload).
    const modal = new WMModal(markRaw(component), props, title, isCloseable, onClose);
    wmData.value[section].push(modal);

    return modal;
}

const addModal = <T extends Component>(component: T, props: ComponentPropsWithoutModal<T>, title: string = '', isCloseable: boolean = false, onClose: ((value: any) => boolean | Promise<boolean>) | null = null) => addWindow('modals', component, props, title, isCloseable, onClose)
const addSideModal = <T extends Component>(component: T, props: ComponentPropsWithoutModal<T>, title: string = '', isCloseable: boolean = false, onClose: ((value: any) => boolean | Promise<boolean>) | null = null) => addWindow('sideModals', component, props, title, isCloseable, onClose)


export const useWM = () => ({
    getWmData,
    openModal: addModal,
    openSideModal: addSideModal,
});